import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import Reveal from "@/components/Reveal";
import Seo from "@/components/Seo";
import { HOTEL } from "@/data/hotel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "mt-2 w-full h-11 px-3 bg-paper-50 border border-ink-900/15 focus:border-sage focus:outline-none";

const WhatWeBring = () => (
  <ul className="mt-6 space-y-5 text-ink-700 leading-relaxed">
    {[
      ["Brand & distribution", "An established Azulo identity, strong online presence, and direct booking channels."],
      ["Sales & marketing", "Dedicated sales professionals plus performance marketing across OTAs, search, and social."],
      ["Operations & manpower", "Trained hospitality staff, SOPs, training, and 24/7 operational support."],
      ["Corporate & MICE pipeline", "Inbound demand from corporate tie-ups and group bookings across India."],
      ["Revenue management", "Dynamic pricing, channel management, and analytics built around your property."],
    ].map(([t, d]) => (
      <li key={t} className="border-l-2 border-sage/50 pl-5">
        <div className="font-serif text-xl text-ink-900">{t}</div>
        <div className="mt-2 text-ink-600">{d}</div>
      </li>
    ))}
  </ul>
);

const PartnerForm = ({ form, update, onSubmit, submitting }) => (
  <form data-testid="partner-form" onSubmit={onSubmit} className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
    <div>
      <label className="overline">Name *</label>
      <input type="text" required value={form.full_name} onChange={update("full_name")} data-testid="partner-name" className={inputCls} />
    </div>
    <div>
      <label className="overline">Email *</label>
      <input type="email" required value={form.email} onChange={update("email")} data-testid="partner-email" className={inputCls} />
    </div>
    <div>
      <label className="overline">Phone</label>
      <input type="tel" value={form.phone} onChange={update("phone")} data-testid="partner-phone" className={inputCls} />
    </div>
    <div>
      <label className="overline">Number of Rooms</label>
      <input type="number" min={1} value={form.number_of_rooms} onChange={update("number_of_rooms")} data-testid="partner-rooms" className={inputCls} />
    </div>
    <div className="md:col-span-2">
      <label className="overline">Property Address *</label>
      <input type="text" required value={form.property_address} onChange={update("property_address")} data-testid="partner-address" className={inputCls} />
    </div>
    <div className="md:col-span-2">
      <label className="overline">Google PIN / Coordinates</label>
      <input
        type="text"
        value={form.google_pin}
        onChange={update("google_pin")}
        data-testid="partner-pin"
        placeholder="e.g. https://maps.app.goo.gl/… or 28.6139, 77.2090"
        className={inputCls}
      />
    </div>
    <div className="md:col-span-2">
      <label className="overline">Describe Your Property *</label>
      <textarea
        required
        rows={5}
        value={form.description}
        onChange={update("description")}
        data-testid="partner-description"
        placeholder="Type, age, current operating model, expectations from the partnership…"
        className="mt-2 w-full p-3 bg-paper-50 border border-ink-900/15 focus:border-sage focus:outline-none resize-none"
      />
    </div>
    <div className="md:col-span-2 mt-2">
      <button
        type="submit"
        disabled={submitting}
        data-testid="partner-submit"
        className="inline-flex items-center h-12 px-8 bg-sage text-paper-50 text-[11px] tracking-[0.22em] uppercase hover:bg-sage-dark transition-colors disabled:opacity-60"
      >
        {submitting ? "Submitting…" : "Submit"}
      </button>
    </div>
  </form>
);

export default function Partner() {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    property_address: "",
    number_of_rooms: "",
    google_pin: "",
    description: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.property_address || !form.description) {
      toast.error("Please fill in the required fields.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        number_of_rooms: form.number_of_rooms ? Number(form.number_of_rooms) : null,
      };
      await axios.post(`${API}/partnerships`, payload);
      setSubmitted(true);
      toast.success("Thanks. Our partnerships team will reach out shortly.");
      setForm({
        full_name: "",
        email: "",
        phone: "",
        property_address: "",
        number_of_rooms: "",
        google_pin: "",
        description: "",
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send right now. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="pt-32 md:pt-40 pb-24" data-testid="partner-page">
      <Seo
        title="Partner With Us — Operate your property with Azulo Hotels"
        description="Have a property suited to a boutique hotel, serviced apartments, or independent residential suites? Partner with Marwon Group — sales, online marketing, end-to-end property management since 1980."
        path="/partner"
      />

      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal>
          <div className="overline">Partner With Azulo Hotels</div>
          <h1 className="mt-6 font-serif text-5xl md:text-7xl leading-[0.98] text-ink-900 max-w-3xl">
            Let&rsquo;s <span className="italic text-sage-dark">talk.</span>
          </h1>
          <p className="mt-6 max-w-3xl text-ink-700 text-lg leading-relaxed">
            If you have a property ideal for use as a boutique hotel, independent residential
            suites, or serviced apartments — we are your ideal partners to maximise its
            potential.
          </p>
          <p className="mt-4 max-w-3xl text-ink-700 leading-relaxed">
            Our sales professionals and online marketing team give your property the boost in
            revenues it needs; our manpower ensures best-in-class hospitality and end-to-end
            property management; and a well-established Azulo brand gives you reach and
            presence across India.
          </p>
          <p className="mt-4 max-w-3xl text-ink-700 leading-relaxed">
            Please send us your details at{" "}
            <a
              href={`mailto:${HOTEL.partnershipsEmail}`}
              className="underline hover:text-sage-dark"
            >
              {HOTEL.partnershipsEmail}
            </a>
            , and our team will reach out to you for possible tie-ups.
          </p>
        </Reveal>

        <div className="mt-16 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <Reveal>
              <h2 className="font-serif text-3xl md:text-4xl text-ink-900 leading-tight">
                What we bring
              </h2>
              <WhatWeBring />
            </Reveal>
          </div>

          <div className="md:col-span-7">
            <Reveal>
              <div className="bg-paper-50 border hairline p-8 md:p-10">
                <h2 className="font-serif text-3xl md:text-4xl text-ink-900">
                  Interested in a tie-up?{" "}
                  <span className="italic text-sage-dark">Fill out the form.</span>
                </h2>

                {submitted ? (
                  <div
                    data-testid="partner-success"
                    className="mt-10 p-8 bg-paper-100 border border-sage/30"
                  >
                    <div className="overline">Inquiry Received</div>
                    <p className="mt-4 text-ink-800 leading-relaxed">
                      Thank you for considering Marwon Group. Our partnerships team will
                      reach out to you shortly to discuss next steps.
                    </p>
                    <button
                      type="button"
                      data-testid="partner-submit-again"
                      onClick={() => setSubmitted(false)}
                      className="mt-6 text-[11px] tracking-[0.22em] uppercase text-sage-dark hover:text-ink-900"
                    >
                      Submit another inquiry →
                    </button>
                  </div>
                ) : (
                  <PartnerForm form={form} update={update} onSubmit={onSubmit} submitting={submitting} />
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </div>
  );
}
